import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../Config';

// Icônes
import {
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiSearch,
  FiFilter,
  FiDownload,
  FiChevronRight,
  FiChevronLeft,
  FiCheck,
  FiX,
  FiUsers,
  FiMail,
  FiPhone,
  FiCalendar,
  FiMoreVertical,
  FiEye
} from 'react-icons/fi';
import {
  HiOutlineAcademicCap,
  HiOutlineOfficeBuilding,
  HiOutlineUserGroup,
  HiOutlineDocumentText,
  HiOutlineExclamationCircle
} from 'react-icons/hi';

const BeneficiairesPage = () => {
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedBeneficiaire, setSelectedBeneficiaire] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    nom: '',
    type: 'ecole',
    contact: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    code_postal: ''
  });
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Configuration axios pour inclure le token
  const api = axios.create({
    baseURL: API_URL,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });

  useEffect(() => {
    fetchBeneficiaires();
  }, []);

  const fetchBeneficiaires = async () => {
    try {
      setLoading(true);
      const response = await api.get('/beneficiaires');
      setBeneficiaires(response.data);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des bénéficiaires', 'error');
      console.error('Error fetching beneficiaires:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
    if (!formData.type) newErrors.type = 'Le type est requis';
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    try {
      const response = await api.post('/beneficiaires', formData);
      setBeneficiaires(prev => [...prev, response.data]);
      showSnackbar('Bénéficiaire créé avec succès', 'success');
      resetForm();
      setOpenDialog(false);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erreur lors de la création', 'error');
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    try {
      const response = await api.put(`/beneficiaires/${selectedBeneficiaire.id}`, formData);
      setBeneficiaires(prev => 
        prev.map(b => b.id === selectedBeneficiaire.id ? response.data : b)
      );
      showSnackbar('Bénéficiaire modifié avec succès', 'success');
      resetForm();
      setOpenDialog(false);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erreur lors de la modification', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/beneficiaires/${selectedBeneficiaire.id}`);
      setBeneficiaires(prev => prev.filter(b => b.id !== selectedBeneficiaire.id));
      showSnackbar('Bénéficiaire supprimé avec succès', 'success');
      setOpenDeleteDialog(false);
      setSelectedBeneficiaire(null);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erreur lors de la suppression', 'error');
    }
  };

  const handleEdit = (beneficiaire) => {
    setSelectedBeneficiaire(beneficiaire);
    setFormData({
      nom: beneficiaire.nom,
      type: beneficiaire.type,
      contact: beneficiaire.contact || '',
      email: beneficiaire.email || '',
      telephone: beneficiaire.telephone || '',
      adresse: beneficiaire.adresse || '',
      ville: beneficiaire.ville || '',
      code_postal: beneficiaire.code_postal || ''
    });
    setOpenDialog(true);
  };

  const handleDeleteClick = (beneficiaire) => {
    setSelectedBeneficiaire(beneficiaire);
    setOpenDeleteDialog(true);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredAndSortedBeneficiaires = useMemo(() => {
    let filtered = beneficiaires.filter(beneficiaire => {
      const matchesSearch = searchTerm === '' || 
        beneficiaire.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (beneficiaire.email && beneficiaire.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (beneficiaire.contact && beneficiaire.contact.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesFilter = filterType === 'all' || beneficiaire.type === filterType;
      
      return matchesSearch && matchesFilter;
    });

    filtered.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [beneficiaires, searchTerm, filterType, sortConfig]);

  const totalPages = Math.ceil(filteredAndSortedBeneficiaires.length / itemsPerPage);
  const paginatedBeneficiaires = filteredAndSortedBeneficiaires.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = () => {
    setFormData({
      nom: '',
      type: 'ecole',
      contact: '',
      email: '',
      telephone: '',
      adresse: '',
      ville: '',
      code_postal: ''
    });
    setErrors({});
    setSelectedBeneficiaire(null);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getTypeConfig = (type) => {
    const configs = {
      ecole: { 
        icon: <HiOutlineAcademicCap className="w-4 h-4" />, 
        label: 'École', 
        color: 'text-blue-600', 
        bg: 'bg-blue-50',
        border: 'border-blue-200'
      },
      association: { 
        icon: <HiOutlineUserGroup className="w-4 h-4" />, 
        label: 'Association', 
        color: 'text-purple-600', 
        bg: 'bg-purple-50',
        border: 'border-purple-200'
      },
      entreprise: { 
        icon: <HiOutlineOfficeBuilding className="w-4 h-4" />, 
        label: 'Entreprise', 
        color: 'text-emerald-600', 
        bg: 'bg-emerald-50',
        border: 'border-emerald-200'
      },
      autre: { 
        icon: <HiOutlineDocumentText className="w-4 h-4" />, 
        label: 'Autre', 
        color: 'text-gray-600', 
        bg: '',
        border: 'border-gray-200'
      }
    };
    return configs[type] || configs.autre;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleExport = () => {
    showSnackbar('Export en cours de développement', 'info');
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Navigation */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-semibold text-gray-900">Bénéficiaires</h1>
              <span className="ml-4 bg-gray-100 text-gray-600 text-sm font-medium px-2.5 py-0.5 rounded-full">
                {beneficiaires.length} au total
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">{beneficiaires.length}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <FiUsers className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Écoles</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">
                  {beneficiaires.filter(b => b.type === 'ecole').length}
                </p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <HiOutlineAcademicCap className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Associations</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">
                  {beneficiaires.filter(b => b.type === 'association').length}
                </p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <HiOutlineUserGroup className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Entreprises</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">
                  {beneficiaires.filter(b => b.type === 'entreprise').length}
                </p>
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg">
                <HiOutlineOfficeBuilding className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher un bénéficiaire..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <FiFilter className="text-gray-400" />
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">Tous les types</option>
                  <option value="ecole">Écoles</option>
                  <option value="association">Associations</option>
                  <option value="entreprise">Entreprises</option>
                  <option value="autre">Autres</option>
                </select>
              </div>
              
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:"
              >
                <FiDownload className="w-4 h-4" />
                <span className="text-sm font-medium">Exporter</span>
              </button>
              
              <button
                onClick={() => {
                  resetForm();
                  setOpenDialog(true);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <FiPlus className="w-4 h-4" />
                <span className="text-sm font-medium">Nouveau</span>
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('nom')}
                  >
                    <div className="flex items-center gap-2">
                      Nom
                      {sortConfig.key === 'nom' && (
                        <span className="text-gray-400">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-2">
                      Date de création
                      {sortConfig.key === 'created_at' && (
                        <span className="text-gray-400">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedBeneficiaires.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="bg-gray-100 p-4 rounded-full mb-4">
                          <FiSearch className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-900 font-medium">Aucun bénéficiaire trouvé</p>
                        <p className="text-gray-500 text-sm mt-1">
                          {searchTerm || filterType !== 'all' 
                            ? 'Essayez de modifier vos critères de recherche' 
                            : 'Commencez par ajouter un nouveau bénéficiaire'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedBeneficiaires.map((beneficiaire) => {
                    const typeConfig = getTypeConfig(beneficiaire.type);
                    return (
                      <tr key={beneficiaire.id} className="hover:">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className={`${typeConfig.bg} p-2 rounded-lg`}>
                              {typeConfig.icon}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {beneficiaire.nom}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: #{beneficiaire.id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${typeConfig.bg} ${typeConfig.color} ${typeConfig.border}`}>
                            {typeConfig.icon}
                            {typeConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {beneficiaire.email && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <FiMail className="w-4 h-4" />
                                {beneficiaire.email}
                              </div>
                            )}
                            {beneficiaire.telephone && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <FiPhone className="w-4 h-4" />
                                {beneficiaire.telephone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <FiCalendar className="w-4 h-4" />
                            {formatDate(beneficiaire.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(beneficiaire)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <FiEdit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(beneficiaire)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                              <FiMoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Affichage de {(currentPage - 1) * itemsPerPage + 1} à{' '}
                {Math.min(currentPage * itemsPerPage, filteredAndSortedBeneficiaires.length)} sur{' '}
                {filteredAndSortedBeneficiaires.length} résultats
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg border transition-colors ${
                    currentPage === 1
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:'
                  }`}
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>
                
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  
                  if (totalPages <= maxVisiblePages) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else if (currentPage <= 3) {
                    for (let i = 1; i <= 4; i++) {
                      pages.push(i);
                    }
                    pages.push('...');
                    pages.push(totalPages);
                  } else if (currentPage >= totalPages - 2) {
                    pages.push(1);
                    pages.push('...');
                    for (let i = totalPages - 3; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    pages.push('...');
                    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                      pages.push(i);
                    }
                    pages.push('...');
                    pages.push(totalPages);
                  }
                  
                  return pages.map((page, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        if (page !== '...') setCurrentPage(page);
                      }}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                        page === '...'
                          ? 'text-gray-400 cursor-default'
                          : currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-700 hover:'
                      }`}
                      disabled={page === '...'}
                    >
                      {page}
                    </button>
                  ));
                })()}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg border transition-colors ${
                    currentPage === totalPages
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:'
                  }`}
                >
                  <FiChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog Création/Modification */}
      {openDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity 0 bg-opacity-75" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    {selectedBeneficiaire ? 'Modifier le bénéficiaire' : 'Nouveau bénéficiaire'}
                  </h3>
                  <button
                    onClick={() => {
                      setOpenDialog(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-b border-gray-200">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.nom ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Nom du bénéficiaire"
                    />
                    {errors.nom && (
                      <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: 'ecole', label: 'École', icon: HiOutlineAcademicCap },
                        { value: 'association', label: 'Association', icon: HiOutlineUserGroup },
                        { value: 'entreprise', label: 'Entreprise', icon: HiOutlineOfficeBuilding },
                        { value: 'autre', label: 'Autre', icon: HiOutlineDocumentText }
                      ].map((type) => {
                        const Icon = type.icon;
                        let selectedStyle = '';
                        let unselectedStyle = 'border-gray-300 hover:';
                        
                        if (formData.type === type.value) {
                          if (type.value === 'ecole') {
                            selectedStyle = 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-100';
                          } else if (type.value === 'association') {
                            selectedStyle = 'border-purple-500 bg-purple-50 text-purple-700 ring-2 ring-purple-100';
                          } else if (type.value === 'entreprise') {
                            selectedStyle = 'border-emerald-500 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-100';
                          } else {
                            selectedStyle = 'border-gray-500  text-gray-700 ring-2 ring-gray-100';
                          }
                        }
                        
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => handleInputChange({ target: { name: 'type', value: type.value } })}
                            className={`flex items-center justify-center gap-2 p-4 border rounded-lg transition-all ${formData.type === type.value ? selectedStyle : unselectedStyle}`}
                          >
                            <Icon className="w-5 h-5" />
                            <span className="text-sm font-medium">{type.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-600">{errors.type}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                          errors.email ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="email@exemple.com"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="+33 1 23 45 67 89"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Adresse
                    </label>
                    <textarea
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      rows="2"
                      placeholder="Adresse complète"
                    />
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4  flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setOpenDialog(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover: transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={selectedBeneficiaire ? handleUpdate : handleCreate}
                  className="px-4 py-2 bg-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <FiCheck className="w-4 h-4" />
                  {selectedBeneficiaire ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog de suppression */}
      {openDeleteDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity 0 bg-opacity-75" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Confirmer la suppression
                  </h3>
                  <button
                    onClick={() => setOpenDeleteDialog(false)}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-b border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-red-100 p-3 rounded-full flex-shrink-0">
                    <HiOutlineExclamationCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-gray-700">
                      Êtes-vous sûr de vouloir supprimer le bénéficiaire{' '}
                      <span className="font-semibold">"{selectedBeneficiaire?.nom}"</span> ?
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Cette action est irréversible et supprimera définitivement toutes les données associées.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4  flex justify-end space-x-3">
                <button
                  onClick={() => setOpenDeleteDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover: transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 rounded-lg text-sm font-medium text-white hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <FiTrash2 className="w-4 h-4" />
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.open && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className={`rounded-lg shadow-lg p-4 max-w-md transform transition-all duration-300 ${
            snackbar.severity === 'success' 
              ? 'bg-emerald-50 border border-emerald-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start">
              <div className={`flex-shrink-0 ${
                snackbar.severity === 'success' ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {snackbar.severity === 'success' ? (
                  <FiCheck className="w-5 h-5" />
                ) : (
                  <HiOutlineExclamationCircle className="w-5 h-5" />
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className={`text-sm font-medium ${
                  snackbar.severity === 'success' ? 'text-emerald-800' : 'text-red-800'
                }`}>
                  {snackbar.message}
                </p>
              </div>
              <button
                onClick={() => setSnackbar(prev => ({ ...prev, open: false }))}
                className="ml-4 flex-shrink-0"
              >
                <FiX className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeneficiairesPage;